import React, { useEffect, useState } from "react";
import { api, loadConfig, MODULES, DEPT_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sliders, Save, Plus, Trash2, ArrowUp, ArrowDown, Building2, Tags, Timer, Truck, Award, ClipboardCheck, Hash, MessageCircle, ShieldCheck } from "lucide-react";
import { ProcessTimeSettings } from "@/components/ProcessTimeSettings";
import { DeliverySettings } from "@/components/DeliverySettings";
import { textOn } from "@/components/StatusBadge";
import { ScanRulesTab } from "@/components/ScanRulesSettings";
import { ScanLine } from "lucide-react";

const SaveBar = ({ onSave, saving, testid }) => (
  <div className="flex justify-end pt-2"><Button onClick={onSave} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid={testid}><Save className="w-4 h-4 mr-1" /> Save</Button></div>
);

const useSetting = (all, key, save) => {
  const [val, setVal] = useState(all[key]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(all[key]); }, [all, key]);
  const doSave = async (v = val) => {
    setSaving(true);
    try { const { data } = await api.put(`/settings/${key}`, { value: v }); setVal(data); await save(key, data); toast.success("Saved"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };
  return [val, setVal, doSave, saving];
};

const CompanyTab = ({ all, save }) => {
  const [v, setV, doSave, saving] = useSetting(all, "company", save);
  if (!v) return null;
  const f = (k, label, ph) => <div><Label>{label}</Label><Input value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} placeholder={ph} data-testid={`sys-company-${k}`} /></div>;
  return (
    <Card className="p-5 space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        {f("name", "Company name", "Rozfa Printing SDN BHD")}{f("whatsapp", "WhatsApp number (60xxxxxxxxx)", "60123456789")}{f("phone", "Phone", "03-xxxx xxxx")}
        <div className="sm:col-span-2">{f("address", "Address", "Full address")}</div>
        <div className="sm:col-span-2"><Label>Footer text (job sheet / labels)</Label><Textarea rows={2} value={v.footer || ""} onChange={(e) => setV({ ...v, footer: e.target.value })} data-testid="sys-company-footer" /></div>
      </div>
      <SaveBar onSave={() => doSave()} saving={saving} testid="sys-company-save" />
    </Card>
  );
};

const StatusesTab = ({ all, save }) => {
  const [v, setV, doSave, saving] = useSetting(all, "statuses", save);
  const [renaming, setRenaming] = useState(null);
  if (!v) return null;
  const upd = (i, p) => setV(v.map((s, j) => (j === i ? { ...s, ...p } : s)));
  const move = (i, d) => { const n = [...v]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x); setV(n); };
  const rename = async (i) => {
    const s = v[i]; const nw = renaming?.value?.trim();
    if (!nw || nw === s.name) return setRenaming(null);
    try { const { data } = await api.post("/settings/statuses/rename", { old: s.name, new: nw }); toast.success(`Renamed • ${data.jobs_updated} job(s) updated`); setRenaming(null); await save("statuses", data.statuses); }
    catch (err) { toast.error(err?.response?.data?.detail || "Rename failed"); }
  };
  return (
    <Card className="p-5 space-y-3">
      <p className="text-xs text-muted-foreground">Order = flow shown on the Production Board & dropdowns. Department decides which queue sees the job; "Done" statuses are treated as closed. To rename a status used by jobs, use the rename action (jobs are updated too).</p>
      <div className="grid grid-cols-[28px_1fr_120px_170px_60px_90px] gap-2 text-[11px] uppercase font-mono text-muted-foreground px-1"><span /><span>Status</span><span>Colour</span><span>Department</span><span>Done</span><span /></div>
      {v.map((s, i) => (
        <div key={i} className="grid grid-cols-[28px_1fr_120px_170px_60px_90px] gap-2 items-center" data-testid={`sys-status-row-${i}`}>
          <span className="w-6 h-6 rounded-full border" style={{ backgroundColor: s.color, color: textOn(s.color) }} />
          {renaming?.i === i ? (
            <div className="flex gap-1"><Input autoFocus value={renaming.value} onChange={(e) => setRenaming({ i, value: e.target.value })} className="h-8" data-testid={`sys-status-rename-input-${i}`} /><Button size="sm" className="h-8" onClick={() => rename(i)} data-testid={`sys-status-rename-save-${i}`}>OK</Button></div>
          ) : all.statuses.some((x) => x.name === s.name) ? (
            <button type="button" className="text-left text-sm font-medium hover:underline" onClick={() => setRenaming({ i, value: s.name })} title="Click to rename" data-testid={`sys-status-name-${i}`}>{s.name}</button>
          ) : (
            <Input value={s.name} onChange={(e) => upd(i, { name: e.target.value })} className="h-8" placeholder="New status name" data-testid={`sys-status-name-input-${i}`} />
          )}
          <div className="flex items-center gap-1"><input type="color" value={s.color} onChange={(e) => upd(i, { color: e.target.value })} className="w-8 h-8 p-0 border rounded cursor-pointer" data-testid={`sys-status-color-${i}`} /><Input value={s.color} onChange={(e) => upd(i, { color: e.target.value })} className="h-8 font-mono text-xs" /></div>
          <Select value={s.dept} onValueChange={(d) => upd(i, { dept: d })}><SelectTrigger className="h-8" data-testid={`sys-status-dept-${i}`}><SelectValue /></SelectTrigger><SelectContent>{Object.entries(DEPT_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
          <Checkbox checked={!!s.done} onCheckedChange={(c) => upd(i, { done: !!c })} data-testid={`sys-status-done-${i}`} />
          <div className="flex gap-0.5">
            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="w-3.5 h-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === v.length - 1} onClick={() => move(i, 1)}><ArrowDown className="w-3.5 h-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => setV(v.filter((_, j) => j !== i))} data-testid={`sys-status-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      ))}
      <div className="flex justify-between pt-2">
        <Button size="sm" variant="outline" onClick={() => setV([...v, { name: "", color: "#e2e8f0", dept: "sales", done: false }])} data-testid="sys-status-add"><Plus className="w-4 h-4 mr-1" /> Add Status</Button>
        <Button onClick={() => doSave()} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="sys-statuses-save"><Save className="w-4 h-4 mr-1" /> Save</Button>
      </div>
    </Card>
  );
};

const TiersTab = ({ all, save }) => {
  const [v, setV, doSave, saving] = useSetting(all, "tiers", save);
  if (!v) return null;
  return (
    <Card className="p-5 space-y-3">
      <p className="text-xs text-muted-foreground">Rename agent tiers and pick their badge colour. Prices per tier are set on each product.</p>
      {v.map((t, i) => (
        <div key={t.key} className="grid grid-cols-[100px_1fr_60px] gap-2 items-center">
          <span className="text-xs font-mono uppercase text-muted-foreground">{t.key}</span>
          <Input value={t.label} onChange={(e) => setV(v.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} data-testid={`sys-tier-label-${t.key}`} />
          <input type="color" value={t.color} onChange={(e) => setV(v.map((x, j) => (j === i ? { ...x, color: e.target.value } : x)))} className="w-10 h-9 p-0 border rounded cursor-pointer" />
        </div>
      ))}
      <SaveBar onSave={() => doSave()} saving={saving} testid="sys-tiers-save" />
    </Card>
  );
};

const QcTab = ({ all, save }) => {
  const [v, setV, doSave, saving] = useSetting(all, "qc_items", save);
  if (!v) return null;
  return (
    <Card className="p-5 space-y-3">
      <p className="text-xs text-muted-foreground">Checklist items that must be ticked before a job leaves the QC / Packing department.</p>
      {v.map((it, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Input value={it.label} onChange={(e) => setV(v.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} data-testid={`sys-qc-label-${i}`} />
          <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => setV(v.filter((_, j) => j !== i))} data-testid={`sys-qc-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      <div className="flex justify-between pt-2">
        <Button size="sm" variant="outline" onClick={() => setV([...v, { key: "", label: "" }])} data-testid="sys-qc-add"><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
        <Button onClick={() => doSave()} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="sys-qc-save"><Save className="w-4 h-4 mr-1" /> Save</Button>
      </div>
    </Card>
  );
};

const JobNumberTab = ({ all, save }) => {
  const [v, setV, doSave, saving] = useSetting(all, "job_number", save);
  if (!v) return null;
  const preview = `${v.prefix || "RZF"}-${new Date().toISOString().slice(2, 10).replace(/-/g, "").slice(0, 6)}-${"1".padStart(v.digits || 3, "0")}`;
  return (
    <Card className="p-5 space-y-3">
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label>Prefix</Label><Input value={v.prefix} onChange={(e) => setV({ ...v, prefix: e.target.value.toUpperCase() })} data-testid="sys-jobno-prefix" /></div>
        <div><Label>Date format (Python strftime)</Label><Input value={v.date_format} onChange={(e) => setV({ ...v, date_format: e.target.value })} data-testid="sys-jobno-date" /><p className="text-[11px] text-muted-foreground mt-1">%y%m%d = 260924 • %Y%m = 202609</p></div>
        <div><Label>Running digits</Label><Input type="number" min="2" max="6" value={v.digits} onChange={(e) => setV({ ...v, digits: Number(e.target.value) })} data-testid="sys-jobno-digits" /></div>
      </div>
      <div className="text-sm">Preview: <span className="font-mono font-bold" data-testid="sys-jobno-preview">{preview}</span></div>
      <SaveBar onSave={() => doSave()} saving={saving} testid="sys-jobno-save" />
    </Card>
  );
};

const WaTab = ({ all, save }) => {
  const [v, setV, doSave, saving] = useSetting(all, "wa_templates", save);
  if (!v) return null;
  const labels = { customer_status: "Customer — job status update", customer_general: "Customer — general", agent_job: "Agent — about a job", agent_general: "Agent — general", agent_to_rozfa: "Agent Portal → Chat Rozfa" };
  return (
    <Card className="p-5 space-y-3">
      <p className="text-xs text-muted-foreground">Placeholders: {"{company} {customer_name} {agent_name} {agent_company} {job_number} {product} {status} {quantity}"}</p>
      {Object.keys(labels).map((k) => (
        <div key={k}><Label>{labels[k]}</Label><Textarea rows={2} value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`sys-wa-${k}`} /></div>
      ))}
      <SaveBar onSave={() => doSave()} saving={saving} testid="sys-wa-save" />
    </Card>
  );
};

const AccessTab = () => {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role !== "agent" && u.role !== "admin")));
  useEffect(() => { load(); }, []);
  const toggle = async (u) => {
    const perms = u.permissions ?? MODULES.map((m) => m.key);
    const next = perms.includes("system") ? perms.filter((p) => p !== "system") : [...perms, "system"];
    await api.patch(`/users/${u.id}`, { permissions: next }); toast.success(`${u.name}: System Settings ${next.includes("system") ? "granted" : "revoked"}`); load();
  };
  return (
    <Card className="p-5 space-y-3">
      <p className="text-xs text-muted-foreground">Rozfa Admin always has full access. Tick staff who may also open System Settings.</p>
      {users.map((u) => {
        const has = (u.permissions ?? MODULES.map((m) => m.key)).includes("system");
        return (
          <label key={u.id} className="flex items-center gap-3 border rounded-md px-3 py-2 cursor-pointer hover:bg-muted/40" data-testid={`sys-access-${u.email}`}>
            <Checkbox checked={has} onCheckedChange={() => toggle(u)} data-testid={`sys-access-toggle-${u.email}`} />
            <div className="text-sm"><div className="font-medium">{u.name}</div><div className="text-xs text-muted-foreground">{u.email} • {u.role}</div></div>
            {has && <span className="ml-auto text-[11px] font-mono uppercase text-emerald-600">system access</span>}
          </label>
        );
      })}
      {users.length === 0 && <div className="text-sm text-muted-foreground">No staff accounts yet.</div>}
    </Card>
  );
};

export default function SystemSettings() {
  const { user } = useAuth();
  const [all, setAll] = useState(null);
  const load = () => api.get("/settings/all").then((r) => setAll(r.data));
  useEffect(() => { load(); }, []);
  const save = async (key, value) => { setAll((a) => ({ ...a, [key]: value })); await loadConfig(); };
  if (!all) return <div className="text-muted-foreground">Loading...</div>;
  const isAdmin = user?.role === "admin";
  const tabs = [
    ["company", "Company", Building2], ["statuses", "Job Statuses", Tags], ["process", "Process Times", Timer], ["delivery", "Delivery", Truck],
    ["tiers", "Agent Tiers", Award], ["qc", "QC Checklist", ClipboardCheck], ["jobno", "Job Number", Hash], ["wa", "WhatsApp", MessageCircle], ["scan", "Scan Rules", ScanLine], ["access", "Access", ShieldCheck],
  ];
  return (
    <div className="space-y-4" data-testid="system-settings-page">
      <div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2"><Sliders className="w-6 h-6" /> System Settings</h1>
        <p className="text-sm text-muted-foreground">Full control of Rozfa PrintOps. Changes apply immediately across staff and Agent Portal.</p>
      </div>
      <Tabs defaultValue="company">
        <TabsList className="flex flex-wrap h-auto justify-start">{tabs.map(([k, l, I]) => <TabsTrigger key={k} value={k} data-testid={`sys-tab-${k}`}><I className="w-3.5 h-3.5 mr-1" />{l}</TabsTrigger>)}</TabsList>
        <TabsContent value="company"><CompanyTab all={all} save={save} /></TabsContent>
        <TabsContent value="statuses"><StatusesTab all={all} save={save} /></TabsContent>
        <TabsContent value="process"><ProcessTimeSettings isAdmin /></TabsContent>
        <TabsContent value="delivery"><DeliverySettings isAdmin /></TabsContent>
        <TabsContent value="tiers"><TiersTab all={all} save={save} /></TabsContent>
        <TabsContent value="qc"><QcTab all={all} save={save} /></TabsContent>
        <TabsContent value="jobno"><JobNumberTab all={all} save={save} /></TabsContent>
        <TabsContent value="wa"><WaTab all={all} save={save} /></TabsContent>
        <TabsContent value="scan"><ScanRulesTab all={all} save={save} /></TabsContent>
        <TabsContent value="access">{isAdmin ? <AccessTab /> : <Card className="p-5 text-sm text-muted-foreground">Only Rozfa Admin can grant System Settings access.</Card>}</TabsContent>
      </Tabs>
    </div>
  );
}
